import { DEVICES } from './../../constants';
import { ICustomElementViewModel, inject, bindable } from 'aurelia';
import { websiteShortCode, environment } from '../../../environment';
import { AuthenticationHandler, Helper } from '../../extensions';
import { INotificationMessage } from '@chicksgroup/types';
import { SessionService } from '../../../services/session-service';
import { CustomerService } from '../../../services/customer-service';
import { User } from '../../../services/models/user';
import './navbar.scss';

@inject(AuthenticationHandler, SessionService, Helper, CustomerService)
export class Navbar implements ICustomElementViewModel {
    @bindable shortcode: string = websiteShortCode() ?? 'CG';
    @bindable user: User;
    environmentLink: string;
    environment = environment();
    notificationData: INotificationMessage[];
    notificationCount: number;
    navbarLogo: string;
    devices = DEVICES;
    width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    canAccessAdminPanel: boolean;
    cartLength: number = 0;
    navbarIsLoaded: boolean = false;

    constructor(private authHandler: AuthenticationHandler, private sessionService: SessionService, private helper: Helper, private customerService: CustomerService) {
        this.environmentLink = this.helper.environmentLink;
        this.navbarLogo = this.helper.dynamicLogo;
        this.checkCanAccessAdminPanel();
        this.getUserNotification();
        this.getUserUnreadCountNotifications();
    }

    async attached() {
        if (this.user) {
            const cartService = await this.sessionService.getCartCount();
            this.cartLength = await cartService;
        }
        setTimeout(() => {
            this.navbarIsLoaded = true;
        }, 500);
        window.addEventListener('resize', this.handleResize);
    }

    detached() {
        window.removeEventListener('resize', this.handleResize);
    }

    handleResize = () => {
        this.width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    };

    redirectToSignIn() {
        void this.authHandler.redirectToSSO({});
    }

    async handleOptionSelected(event: CustomEvent) {
        const adminUrls: Record<string, string> = {
            'dev': 'https://dev-legacy-admin.chicksgold.com',
            'staging': 'https://staging-legacy-admin.chicksgold.com',
            'production': 'https://admin.chicksgold.com',
        };

        const routes: Record<string, string> = {
            'Profile': '/customer-portal/profile',
            'Subscription': '/customer-portal/subscription',
            'Orders': '/customer-portal/orders',
            'Balance': '/customer-portal/balance',
            'Verification': '/customer-portal/verification',
            'Mobile Verification': '/customer-portal/verification',
            'Email Verification': '/customer-portal/verification',
            'ID Verification': '/customer-portal/verification',
            'Address Verification': '/customer-portal/verification',
            'Purchased': '/customer-portal/purchased',
            'Swap': '/customer-portal/swap',
            'Sold': '/customer-portal/sold',
            'Security': '/customer-portal/security',
            'Reset Password': '/customer-portal/security/reset-password',
            '2FA Settings': '/customer-portal/security/2fa',
            'Withdraw Password': '/customer-portal/security/withdraw',
            'Support': '/customer-portal/support',
            'Admin Panel': adminUrls[this.environment ?? 'production']
        };

        const selectedOption = event.detail;

        if (selectedOption === 'Sign Out') {
            await this.sessionService.logout();
            return window.location.reload();
        }

        const path = routes[selectedOption];
        if (path) {
            return window.location.href = selectedOption === 'Admin Panel' ? path : `${this.environmentLink}${path}`;
        }

        return null;
    }

    async getUserUnreadCountNotifications(): Promise<number> {
        return this.notificationCount = this.user ? await this.customerService.getNotificationCount() as number : 0;
    }

    async getUserNotification(): Promise<INotificationMessage[]> {
        return this.notificationData = this.user ? await this.customerService.getNotifications() as INotificationMessage[] : [];
    }

    checkCanAccessAdminPanel(): boolean {
        this.canAccessAdminPanel = this.sessionService.checkRolesForPanelAccess(this.user);
        return this.canAccessAdminPanel;
    }

    viewAsAdmin(isAdmin: boolean) {
        return this.sessionService.saveAdminView(isAdmin);
    }

    setCurrencyMobile(currencyEvent: CustomEvent) {
        this.sessionService.saveCurrency(currencyEvent.detail);
    }

    setLanguageMobile(languageEvent: CustomEvent) {
        this.sessionService.saveLanguage(languageEvent.detail.name);
    }

    get isShortCodeCX(): boolean {
        return this.shortcode === 'CX';
    }
};
